/**
 * Oct 20 addendum L1 (WS-FIX3) — the ticket menu for a viewer whose card is READ-ONLY (a floor worker without assign
 * rights looking at a column that is not theirs: a Detail-Specialist-only member, or a technician on another
 * technician's column). The full `TicketKebabMenu` stays hidden there; this offers exactly ONE item — "Move Vehicle…" —
 * which opens the same Move Vehicle dialog (with the L5 "who moved it" attestation) through the same location
 * authority as everywhere else.
 *
 * Gated by the client predicate `canRelocateVehicles`; the server (`can_relocate_vehicles_for_shop` inside
 * `move_vehicle_location`) remains the authority. Grants nothing else: no assign, timer, edit, pickup or delete.
 */
import { useState } from 'react';
import { MoreVertical, Move } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { FohMoveVehicleDialog } from '@/components/foh/workboard/FohMoveVehicleDialog';

export function TicketRelocateOnlyMenu({ ticketId, status }: { ticketId: string; status?: string }) {
  const { canRelocateVehicles } = usePermissions();
  const { activeShop } = useAuth();
  const shopId = activeShop?.id || '';
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  if (!canRelocateVehicles || !shopId || status === 'Completed' || isTicketDeleted({ status })) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
            onClick={(e) => e.stopPropagation()}
            data-testid="ticket-kebab-menu"
            aria-label="Ticket options menu"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-48"
          data-testid="ticket-kebab-dropdown"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid="kebab-move-vehicle">
            <Move className="mr-2 h-4 w-4" />
            Move Vehicle…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <FohMoveVehicleDialog
        open={open}
        ticketId={open ? ticketId : null}
        shopId={shopId}
        onClose={() => setOpen(false)}
        onMoved={(id) => {
          toast({ title: 'Vehicle moved' });
          queryClient.invalidateQueries({ queryKey: ['tickets'] });
          queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
          queryClient.invalidateQueries({ queryKey: ['ticket-location-history', id] });
          void notifyDayboardInvalidated({ reason: 'kebab-move-vehicle', shopId, ticketId: id });
        }}
      />
    </>
  );
}
